import {
  AlertCircle,
  Archive,
  BookOpen,
  BookSearch,
  CalendarSync,
  Download,
  LayoutDashboard,
  Mail,
  MousePointerClick,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "../Sidebar/Sidebar";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.webp";
import { HStack } from "../../layout/HStack";
import styles from "./AppSidebar.module.css";
import { ViewSwitch } from "@/components/elements/ViewSwitcher/ViewSwitcher";
import { UserMenu } from "@/components/elements/UserMenu/UserMenu";
import { useView } from "@/context/viewContext";
import { useAuth } from "@/context/authContext";

const adminItems = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Rotations",
    url: "/rotations",
    icon: CalendarSync,
  },
  {
    title: "Assign Preferences",
    url: "/assign",
    icon: MousePointerClick,
  },
  {
    title: "Explorations",
    url: "/explorations",
    icon: BookOpen,
  },
  {
    title: "Emails",
    url: "/emails",
    icon: Mail,
  },
  {
    title: "Archive",
    url: "/rotations/archive",
    icon: Archive,
  },
];

const sysAdminItems = [
  ...adminItems,
  {
    title: "Users",
    url: "/users",
    icon: Users,
  },
  {
    title: "Exports",
    url: "/exports",
    icon: Download,
  },
  {
    title: "Error Logs",
    url: "/error-logs",
    icon: AlertCircle,
  },
];

const teacherItems = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Rotations",
    url: "/rotations",
    icon: CalendarSync,
  },
  {
    title: "Advisees",
    url: "/advisees",
    icon: Users,
  },
  {
    title: "My Explorations",
    url: "/explorations/me",
    icon: BookOpen,
  },
  {
    title: "Browse Explorations",
    url: "/explorations",
    icon: BookSearch,
  },
  {
    title: "Archive",
    url: "/rotations/archive",
    icon: Archive,
  },
];

const studentItems = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Sign Up",
    url: "/rotations",
    icon: CalendarSync,
  },
  {
    title: "My Explorations",
    url: "/explorations/me",
    icon: BookOpen,
  },
  {
    title: "Browse Explorations",
    url: "/explorations",
    icon: BookSearch,
  },
];

const AppSidebar = ({
  activePage,
  ...props
}: React.ComponentProps<typeof Sidebar> & {
  activePage?: string;
}) => {
  const { state } = useAuth();
  const { viewAs } = useView();

  const activeItems =
    viewAs === "sys-admin"
      ? sysAdminItems
      : viewAs === "administrator"
        ? adminItems
        : viewAs === "teacher"
          ? teacherItems
          : viewAs === "student"
            ? studentItems
            : studentItems;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className={styles.header}>
        <HStack justify="space-between" align="center">
          <img
            src={logo}
            style={{ width: "140px", height: "24.5px", marginLeft: "0.5rem" }}
          />
          <SidebarTrigger />
        </HStack>
        {state.user!.role !== "student" && <ViewSwitch />}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {activeItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={{ children: item.title }}
                  isActive={activePage === item.url}
                  asChild
                >
                  <Link to={item.url} style={{ textDecoration: "none" }}>
                    <item.icon />
                    {item.title}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  );
};

export { AppSidebar };
